import { useEffect, useCallback } from 'react';

/**
 * Hook to handle ESC key press to close modals/dialogs
 * @param isOpen - Whether the modal is currently open
 * @param onClose - Callback to close the modal
 * @param enabled - Optional flag to disable the hook (default: true)
 */
export function useEscapeKey(
  isOpen: boolean,
  onClose: () => void,
  enabled: boolean = true
) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && enabled) {
        event.preventDefault();
        onClose();
      }
    },
    [isOpen, onClose, enabled]
  );

  useEffect(() => {
    if (!isOpen || !enabled) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, enabled, handleKeyDown]);
}

/**
 * Hook to handle multiple keyboard shortcuts
 * @param shortcuts - Map of key combinations to callbacks
 * @param enabled - Optional flag to disable the hook (default: true)
 *
 * @example
 * useKeyboardShortcuts({
 *   'Escape': handleClose,
 *   'ctrl+s': handleSave,
 *   'ctrl+Enter': handleSubmit,
 * });
 */
export function useKeyboardShortcuts(
  shortcuts: Record<string, () => void>,
  enabled: boolean = true
) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Build the key combination string
      const parts: string[] = [];
      if (event.ctrlKey || event.metaKey) parts.push('ctrl');
      if (event.altKey) parts.push('alt');
      if (event.shiftKey) parts.push('shift');
      parts.push(event.key);

      const keyCombo = parts.join('+');
      const keyComboLower = keyCombo.toLowerCase();

      // Check for matching shortcut
      for (const [shortcut, callback] of Object.entries(shortcuts)) {
        if (shortcut.toLowerCase() === keyComboLower || shortcut === event.key) {
          event.preventDefault();
          callback();
          break;
        }
      }
    },
    [shortcuts, enabled]
  );

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);
}
